import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { Lock, Mail, Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";

export default function AdminLogin() {
  const { user, initializing, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (initializing) return null;
  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f172a] px-4 py-12"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF9933] to-[#E67300] shadow-lg shadow-[#FF9933]/30 mb-4">
            <ShieldCheck className="w-8 h-8 text-white" strokeWidth={2.2} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "Eczar, serif" }}>
            Kumbh 2028 Admin
          </h1>
          <p className="text-sm text-white/50">Sign in to access the dashboard</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
            {error && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="username"
                  placeholder="admin@example.com"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none text-gray-900 text-sm transition-colors"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-200 focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none text-gray-900 text-sm transition-colors"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-[#FF9933] to-[#E67300] hover:from-[#E67300] hover:to-[#CC6600] text-white font-semibold text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-100 text-center">
            <a
              href="/"
              className="text-xs text-gray-500 hover:text-[#FF9933] transition-colors"
              data-testid="back-to-site-link"
            >
              ← Back to public site
            </a>
          </div>
        </div>

        <p className="text-center text-xs text-white/30 mt-6">
          Community volunteer initiative. Not affiliated with Government of MP.
        </p>
      </div>
    </div>
  );
}
